import React, { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { selectHasAnyGroup } from '../../../../redux/selector/system'
import ButtonsModal from '../../../Elements/modal/ButtonsModal'
import { checkIsTodayOrder } from '../../../../helper/checkIsTodayOrder'
import { convert2CouponItem, reorderButtonsForUnpaidOrder } from '../../../../helper'
import { setPickupMonitorSelectedOrder, setPickupMonitorShowCancelOrder, setPickupMonitorShowOptionsModal, setPickupMonitorShowOrderDataModal, setPickupMonitorShowOrderModal } from '../../../../redux/action/pickupMonitor'
import { setAppEventZIndex } from '../../../../redux/action/system'
import { updateOrderPickupStatusThunk } from '../../../../redux/thunk/pickupMonitor'
import { printRequestsThunk } from '../../../../redux/thunk/order/printRequests'
import { useLocation, useNavigate } from 'react-router-dom'
import { getExtraDiscountPrice } from '../../../../redux/selector/cart/helper'
import { setCartExtraDiscount } from '../../../../redux/action/cartDiscount'
import { setCashDeskItemFocusIndex, setCashDeskItems, setCashDeskOrder, setCashDeskOrderType } from '../../../../redux/action/cashDesk'
import { setCustomer } from '../../../../redux/action/customer'
import { setDeliveryCartCurrentDeliveryCost, setDeliveryCartItemFocusIndex, setDeliveryCartItems, setDeliveryCartOrder, setDeliveryCartOrderType } from '../../../../redux/action/deliveryCart'
import { getCouponThunk } from '../../../../redux/thunk/coupon'
import { prepareItemsForCoupon } from '../../../../helper/prepareItemsForCoupon'
import { isFullyPaidOrder } from '../../../Elements/table/OrdersTable/helper'
import { useCancellationWindow } from '../../../../hooks/useCancellationWindow'

const EVENT_Z_INDEX = 1

const ModalOrder = React.memo(() => {
	const { t } = useTranslation()
	const selectedOrder = useSelector(store => store.pickupMonitor.selectedOrder)
	const showOrderModal = useSelector(store => store.pickupMonitor.modal.showOrder)
	const hasRoleCancelOrder = useSelector(selectHasAnyGroup(['OWNER', 'SUPERADMIN', 'ADMIN', 'CALL_AGENT']))
	const isUnpaidOrder = selectedOrder?.payments?.some(payment => !payment.payed)
	const dispatch = useDispatch()
	const isPaid = selectedOrder?.payments?.filter(item => item?.method === selectedOrder?.paymentMethod)[0]?.payed
	const { pathname } = useLocation()
	const [, section] = pathname.split('/')
	const module = section === 'delivery' ? 'DELIVERY' : 'CASH_DESK'
	const navigate = useNavigate()
	const { canCancelOrder } = useCancellationWindow()

	const showOrderDetails = () => {
		dispatch(setPickupMonitorShowOrderDataModal(true))
		dispatch(setPickupMonitorShowOrderModal(false))
	}

	const printOrderClick = useCallback(async () => {
		if (isPaid) {
			await dispatch(printRequestsThunk(selectedOrder.orderId, 'CUSTOMER_RECEIPT_DUPLICATE', false))
			await dispatch(printRequestsThunk(selectedOrder.orderId, 'KITCHEN_RECEIPT'))
		} else {
			await dispatch(printRequestsThunk(selectedOrder.orderId, 'PRELIMINARY_RECEIPT'))
		}
		dispatch(setPickupMonitorShowOrderModal(false))
	}, [dispatch, selectedOrder?.orderId, isPaid])

	const handleClose = useCallback(() => {
		dispatch(setPickupMonitorShowOrderModal(false))
		dispatch(setPickupMonitorSelectedOrder(null))
		dispatch(setAppEventZIndex(0))
	}, [dispatch])

	const cancelOrder = useCallback(() => {
		dispatch(setPickupMonitorShowOrderModal(false))
		dispatch(setPickupMonitorShowCancelOrder(true))
		dispatch(setAppEventZIndex(2))
	}, [dispatch])

	const handleOrderPickupStatus = useCallback(async (updatableStatus = null) => {
		if (isUnpaidOrder) {
			dispatch(setPickupMonitorSelectedOrder(selectedOrder))
			dispatch(setPickupMonitorShowOrderModal(false))
			dispatch(setPickupMonitorShowOptionsModal(true))
		} else {
			await dispatch(updateOrderPickupStatusThunk(selectedOrder.orderId, updatableStatus))
			handleClose()
		}
	}, [selectedOrder, dispatch, handleClose, isUnpaidOrder])

	const convertItemsWithCoupon = useCallback(async (items) => {
		const { resultItems, marketingCoupon } = prepareItemsForCoupon(items)
		if (marketingCoupon) {
			const coupon = await dispatch(getCouponThunk(marketingCoupon.itemId))
			const convertItem = convert2CouponItem(coupon, marketingCoupon.status)
			return [...resultItems, convertItem]
		}
		return resultItems
	}, [dispatch])

	const changeCashDeskOrder = useCallback(async (preparedItems) => {
		dispatch(setCashDeskOrderType(selectedOrder.orderType))
		dispatch(setCashDeskOrder(selectedOrder))
		dispatch(setCashDeskItems(preparedItems))
		dispatch(setCashDeskItemFocusIndex(null))
		navigate('/cashdesk', { state: 'pickupMonitor' })
	}, [selectedOrder, dispatch, navigate])

	const changeDeliveryOrder = useCallback(async (preparedItems) => {
		const customer = selectedOrder.customer ? { ...selectedOrder.customer, deliveryCost: selectedOrder.deliveryCost?.cost } : null
		dispatch(setCustomer(customer))
		dispatch(setDeliveryCartOrderType(selectedOrder.orderType))
		dispatch(setDeliveryCartOrder(selectedOrder))
		dispatch(setDeliveryCartItems(preparedItems))
		dispatch(setDeliveryCartItemFocusIndex(null))
		dispatch(setDeliveryCartCurrentDeliveryCost(selectedOrder.deliveryCost?.cost))
		navigate('/delivery/cart', { state: 'pickupMonitor' })
	}, [dispatch, selectedOrder, navigate])

	const changeOrder = useCallback(async () => {
		const categoryDiscountItems = (selectedOrder.purchaseItems || []).filter(item => item.type === 'CATEGORY_DISCOUNT')
		const preparedItems = await convertItemsWithCoupon([...selectedOrder.items, ...categoryDiscountItems])
		const extraDiscount = getExtraDiscountPrice(selectedOrder.items)
		dispatch(setCartExtraDiscount(Math.abs(extraDiscount)))
		dispatch(setPickupMonitorShowOrderModal(false))
		if (module === 'DELIVERY') {
			changeDeliveryOrder(preparedItems)
		} else {
			changeCashDeskOrder(preparedItems)
		}
	}, [selectedOrder, convertItemsWithCoupon, dispatch, module, changeDeliveryOrder, changeCashDeskOrder])

	const isDenied = selectedOrder?.status?.status === 'DENIED'
	const isCanceled = selectedOrder?.status?.status === 'CANCELED'
	const isAccepted = selectedOrder?.status?.status !== 'NOT_ACCEPTED'
	const isRegistered = selectedOrder?.status?.status !== 'NOT_REGISTERED'
	const isClosed = !!selectedOrder?.zNumber
	const orderDate = selectedOrder?.preOrder ? selectedOrder?.preOrder : selectedOrder?.orderDate
	const isTodayOrder = checkIsTodayOrder(orderDate)
	const isInvoiceOrder = selectedOrder?.payments?.find(p => p.method === 'INVOICE')
	const isMoneyExchange = selectedOrder?.items?.find(item => item.type === 'CASH_EXCHANGE')
	const hasPlatform = !!selectedOrder?.platformMetaData
	const isNotPlatformOnlineShop = hasPlatform && selectedOrder?.platformMetaData?.platformType !== 'ONLINE_SHOP'
	const isPartiallyPaidWithGiftCoupon = !!selectedOrder?.payments?.find(p => p.method === 'GIFT_COUPON')
	const isUnpaidButPartiallyPaidWithCoupons = !isPaid && isPartiallyPaidWithGiftCoupon
	const isGiftCouponItem = selectedOrder?.items?.filter((el) => el?.type === 'GIFT_COUPON')?.[0]
	const conditionForPrint = !isCanceled && !isDenied && isAccepted && isRegistered && isTodayOrder
	const conditionForChange = !isCanceled && !isDenied && !isNotPlatformOnlineShop && !isClosed && isRegistered && !isInvoiceOrder && !isMoneyExchange && !isUnpaidButPartiallyPaidWithCoupons && !isGiftCouponItem
	const isAwaitingPickupOrder = selectedOrder?.pickupMetaData?.status === 'AWAITING_PICKUP'
	const isProcessingPickupOrder = selectedOrder?.pickupMetaData?.status === 'PROCESSING_PICKUP'

	const buttons = [
		{ icon: 'payment', text: t('order.modal.order_pay'), onClick: handleOrderPickupStatus, show: !isCanceled && !isDenied && isAccepted && isRegistered && !isPaid },
		{ icon: 'show', text: t('order.modal.show_order'), onClick: showOrderDetails, show: true },
		{ icon: 'print', text: t('order.modal.print_order'), onClick: printOrderClick, show: conditionForPrint },
		{ icon: 'cancel', text: t('order.modal.cancel_order'), onClick: cancelOrder, show: !isCanceled && !isDenied && isAccepted && canCancelOrder(selectedOrder) && isRegistered && hasRoleCancelOrder && isFullyPaidOrder(selectedOrder) && !isMoneyExchange },
		{ icon: 'change', text: t('order.modal.change'), onClick: changeOrder, show: conditionForChange },
		{ icon: 'checkmark', text: t('order.modal.ready_for_pickup'), onClick: () => handleOrderPickupStatus('PROCESSING_PICKUP'), show: isAwaitingPickupOrder && isPaid },
		{ icon: 'checkmark', text: t('order.modal.picked_up'), onClick: () => handleOrderPickupStatus('PICKED_UP'), show: isProcessingPickupOrder }
	]

	const sortedButtons = isPaid ? buttons : reorderButtonsForUnpaidOrder(buttons, t)

	return (
		<ButtonsModal isOpen={showOrderModal && selectedOrder}
									close={handleClose}
									id="pickup-monitor-order-modal"
									title={(selectedOrder?.orderDayCounter ?
										t('order.modal.number_order') + ' #' + selectedOrder?.orderDayCounter
										: t('order.modal.without_number'))}
									zIndex={EVENT_Z_INDEX}
									returnZIndex={0}
									size="small"
									buttons={sortedButtons} />
	)
})

ModalOrder.displayName = 'ModalOrder'
export default ModalOrder
